import { FC, useEffect, useState } from 'react'

import { zodResolver } from '@hookform/resolvers/zod'
import cn from 'clsx'
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form'

import {useAppDispatch, useAppSelector} from "@App/hooks";
import { CloseIcon } from '@App/static/icons/ArrowIcons'

import {sendCreatePriceAlert} from "@Redux/slices/sockets/ProxyServiceConnectionSlice";
import {selectPriceAlertsList} from "@Redux/slices/trade/trades/selectors";

import {Wallet} from "@Interfaces/CommonInterfaces";

import { MarketIcon } from '@UI/crypto_exchange/desktop/components/common/MarketIcon'
import { Tabs } from '@UI/crypto_exchange/desktop/components/common/Tabs'
import { Button } from '@UI/crypto_exchange/desktop/components/controls/Button'
import { AlertForm } from '@UI/crypto_exchange/desktop/pages/spot/components/PriceAlert/components/AlertForm/AlertForm'
import { PriceAlerts } from '@UI/crypto_exchange/desktop/pages/spot/components/PriceAlert/components/CreatedPriceAlert/PriceAlerts'
import {
  ALERT_TYPE_TABS,
  CREATED_ALERTS_TABS,
} from '@UI/crypto_exchange/desktop/pages/spot/components/PriceAlert/constants'
import {
  AlertType,
  ALERTS_CONTROL,
  FormData,
  formSchema,
} from '@UI/crypto_exchange/desktop/pages/spot/components/PriceAlert/types'

import s from './styles.module.scss'

type PriceAlertProps = {
  isCollapsedPriseAlert: boolean
  targetCurrency: string
  sourceCurrency: string
  marketId: string
  marketSymbol: string
  priseAlertToggle: () => void
  wallet: Wallet
}

export const PriceAlert: FC<PriceAlertProps> = ({
  isCollapsedPriseAlert,
  priseAlertToggle,
  sourceCurrency,
  targetCurrency,
  marketId,
  marketSymbol,
  wallet,
}) => {
  const dispatch = useAppDispatch()
  const priceAlerts = useAppSelector(selectPriceAlertsList)
  const [createdAlerts, setCreatedAlerts] = useState<AlertType[]>([])
  const [triggeredAlerts, setTriggeredAlerts] = useState<AlertType[]>([])
  const [activeCreatedTab, setActiveCreatedTab] = useState<ALERTS_CONTROL>(CREATED_ALERTS_TABS[0].value)

  const { setValue, handleSubmit, control, ...methods } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      execution_price: '',
      note: '',
      side: ALERT_TYPE_TABS[0].value,
      market_id: marketId,
    },
    mode: 'all',
  })

  const onClose = () => {
    priseAlertToggle()
  }

  const onSubmit: SubmitHandler<FormData> = (data: FormData) => {
    dispatch(
      sendCreatePriceAlert({
        market_id: marketId,
        wallet_id: wallet.id,
        side: data.side,
        price: data.execution_price,
        note:  data.note
      }),
    )
    setValue('note', '')
  }

  useEffect(() => {
    if (priceAlerts) {
      const activeAlerts: AlertType[] = []
      const triggeredAlerts: AlertType[] = []

      priceAlerts.forEach((item: AlertType) => {
        if (String(item.market_id) !== String(marketId)) {
          return
        }

        if (item.completed_at) {
          triggeredAlerts.push(item)
        } else {
          activeAlerts.push(item)
        }
      })
      setCreatedAlerts(activeAlerts)
      setTriggeredAlerts(triggeredAlerts)
    }

  }, [marketId, priceAlerts, isCollapsedPriseAlert])

  return (
    <div className={cn(s.container, !isCollapsedPriseAlert && s.containerHidden)}>
      <div className={s.headerContainer}>
        <div className={s.headerImgContainer}>
          <MarketIcon
            symbol={marketSymbol}
            source_currency={sourceCurrency}
            target_currency={targetCurrency}
            className={s.marketIcon}
          />
        </div>
        <div>
          <span className={s.source}>{sourceCurrency}</span>
          <span className={s.target}>/{targetCurrency}</span>
        </div>

        <CloseIcon className={s.closeIcon} onClick={onClose} />
      </div>

      <h3>Price alerts</h3>

      <FormProvider {...methods} setValue={setValue} handleSubmit={handleSubmit} control={control}>
        <AlertForm targetCurrency={targetCurrency} sourceCurrency={sourceCurrency} onSubmit={onSubmit}>
          <Button size="small" type="submit">
            Add alert
          </Button>
        </AlertForm>
      </FormProvider>

      <Tabs<ALERTS_CONTROL>
        tabsClassName={s.tabsClassName}
        tabs={CREATED_ALERTS_TABS}
        activeTab={activeCreatedTab}
        tabsFillSpace
        setActiveTab={setActiveCreatedTab}
      />

      {(!!createdAlerts.length || !!triggeredAlerts.length) && (
        <PriceAlerts
          alerts={activeCreatedTab === ALERTS_CONTROL.ACTIVE ? createdAlerts : triggeredAlerts}
          targetCurrency={targetCurrency}
          sourceCurrency={sourceCurrency}
        />
      )}
    </div>
  )
}
